import React, { useRef, useState } from 'react';

import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogActions,
  ActionableDialogContent,
  ActionableDialogTitle,
  ContainerProps,
} from '@components/Dialog';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import { unstable_batchedUpdates } from 'react-dom';

const strings = createStrings({
  ja: {
    pleaseConfirm: 'ご確認',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  dialogContent: {
    ...common.textGray700,
    ...common.middleText,
    padding: '1.5rem 1.5rem',
  },
}));

export interface ConfirmMessage {
  title?: React.ReactNode;
  message?: React.ReactNode;
  /**
   * Alias to `message`
   */
  messages?: React.ReactNode;
}

export interface UseConfirmDialogOptions {
  initialOpen?: boolean;
  initialConfirm?: ConfirmMessage;
  actionableDialogProps?: Partial<ContainerProps>;
}

export type ConfirmResult = 'confirmed' | 'cancelled';

export const useConfirmDialog = (options: UseConfirmDialogOptions = {}) => {
  const { initialOpen = false, initialConfirm = {}, actionableDialogProps } = options;

  const [open, setOpen] = useState(initialOpen);

  const [confirmMessage, setConfirmMessage] = useState(initialConfirm);

  const onClickConfirmRef = useRef(() => {});
  const onClickCancelRef = useRef(() => {});

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return {
    renderDialog() {
      return (
        <ActionableDialog open={open} onClose={onClickCancelRef.current} {...actionableDialogProps}>
          <ActionableDialogTitle>
            {confirmMessage.title ?? strings.pleaseConfirm}
          </ActionableDialogTitle>
          <ActionableDialogContent className={clsx(classes.dialogContent)}>
            {confirmMessage.message ?? confirmMessage.messages}
          </ActionableDialogContent>
          <ActionableDialogActions>
            <Button
              color="primary"
              text={formStrings.confirm}
              size="small"
              type="button"
              onClick={onClickConfirmRef.current}
            />
            <Button
              color="default"
              text={formStrings.cancel}
              size="small"
              type="button"
              onClick={onClickCancelRef.current}
            />
          </ActionableDialogActions>
        </ActionableDialog>
      );
    },
    async confirm(confirm: ConfirmMessage): Promise<ConfirmResult> {
      unstable_batchedUpdates(() => {
        setOpen(true);
        setConfirmMessage(confirm);
      });

      const result = await new Promise<ConfirmResult>((resolve) => {
        onClickConfirmRef.current = () => resolve('confirmed');
        onClickCancelRef.current = () => resolve('cancelled');
      });

      setOpen(false);
      return result;
    },
  };
};
